import type { UserType } from '../../types/role.types';
import type { CreateResidentFailure, CreateResidentRequest } from '../../api/frontDeskUsers';

/**
 * The residential user types of the tenant (owner, resident, property manager), found by name like the
 * server does: the whole name, so «Non-resident» or «Resident Guest» never match. The Front Desk creates only
 * these: the server answers 403 to any other type.
 */
export const residentialUserTypes = (types: UserType[]): UserType[] =>
  types.filter((t) => /^(owner|resident|property[\s_-]?manager)$/i.test(t.name.trim()));

export interface ResidentForm {
  email: string;
  name: string;
  family_name: string;
  phone_number: string;
  user_type_id: string;
  estate_id: string;
}

export const EMPTY_RESIDENT: ResidentForm = { email: '', name: '', family_name: '', phone_number: '', user_type_id: '', estate_id: '' };

export type ResidentIssue = 'email' | 'name' | 'family_name' | 'user_type' | 'estate';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** What still blocks «Create resident». The phone is optional; when given it must look like an E.164 number. */
export const residentIssues = (f: ResidentForm): ResidentIssue[] => {
  const issues: ResidentIssue[] = [];
  if (!EMAIL_RE.test(f.email.trim())) issues.push('email');
  if (!f.name.trim()) issues.push('name');
  if (!f.family_name.trim()) issues.push('family_name');
  if (!f.user_type_id) issues.push('user_type');
  if (!f.estate_id) issues.push('estate');
  return issues;
};

/** A phone that is only a dial code, or has too few digits, is treated as not given. */
const cleanPhone = (phone: string): string => {
  const p = phone.trim();
  return p.replace(/\D/g, '').length >= 8 ? p : '';
};

/** The POST body. No password (Cognito sends the invitation), no group, no custom attributes: the server refuses them. */
export const residentRequest = (f: ResidentForm): CreateResidentRequest => {
  const phone = cleanPhone(f.phone_number);
  return {
    email: f.email.trim().toLowerCase(),
    name: f.name.trim(),
    family_name: f.family_name.trim(),
    ...(phone ? { phone_number: phone } : {}),
    user_type_id: Number(f.user_type_id),
    estate_id: Number.isNaN(Number(f.estate_id)) ? f.estate_id : Number(f.estate_id),
  };
};

export const residentFailureMessage = (failure: CreateResidentFailure): string => {
  switch (failure) {
    case 'email_exists': return 'A user with this email already exists.';
    case 'invalid': return 'Check the fields: the server did not accept the email, phone, type or residence.';
    case 'forbidden': return 'You are not allowed to create this kind of user.';
    default: return 'The resident could not be created. Try again.';
  }
};
